import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/QuadPanel';
import SectionHeader from 'features/IRWorkstation/components/SectionHeader';
import SalesforceTaskRow from 'features/IRWorkstation/components/SalesforceTaskRow';
import useSalesforceTasks from 'hooks/useSalesforceTasks';

// group tasks by due date into time-based sections
const groupTasksByDueDate = (tasks) => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const endOfWeek = new Date(today);
	endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

	const overdue = [];
	const dueThisWeek = [];
	const upcoming = [];

	tasks.forEach((task) => {
		if (!task.due_date) {
			upcoming.push(task);
			return;
		}

		const dueDate = new Date(task.due_date + 'T00:00:00');
		if (dueDate < today) {
			overdue.push(task);
		} else if (dueDate <= endOfWeek) {
			dueThisWeek.push(task);
		} else {
			upcoming.push(task);
		}
	});

	const sections = [];
	if (overdue.length > 0) {
		sections.push({ section: 'Overdue', items: overdue });
	}
	if (dueThisWeek.length > 0) {
		sections.push({ section: 'Due this week', items: dueThisWeek });
	}
	if (upcoming.length > 0) {
		sections.push({ section: 'Upcoming', items: upcoming });
	}
	return sections;
};

const WorkQueueWidget = ({ onOpenTaskDrawer }) => {
	const {
		tasks: salesforceTasks,
		loading: sfLoading,
		error: sfError,
	} = useSalesforceTasks();

	const workQueueSections = groupTasksByDueDate(salesforceTasks);

	return (
		<QuadPanel
			title='Work Queue'
			badge={`${salesforceTasks.length} open`}
			badgeColor={salesforceTasks.length > 0 ? 'red' : 'gray'}
			subtitle={
				sfError === 'not_connected' || sfError === 'reconnect_required'
					? 'Connect Salesforce to see live tasks'
					: 'Live from Salesforce'
			}
		>
			{sfLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={24} />
				</Box>
			) : sfError ? (
				<Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						{sfError === 'not_connected'
							? 'Connect your Salesforce account to view tasks here.'
							: sfError === 'reconnect_required'
								? "Connection expired. Please remove and reconnect your Salesforce account from Launchpad's Connections page."
								: 'Unable to load tasks. Please try again later.'}
					</Typography>
				</Box>
			) : salesforceTasks.length === 0 ? (
				<Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						No open tasks
					</Typography>
				</Box>
			) : (
				workQueueSections.map((workQueueSection) => (
					<Box key={workQueueSection.section}>
						<SectionHeader label={workQueueSection.section} />
						{workQueueSection.items.map((task) => (
							<SalesforceTaskRow
								key={task.id}
								task={task}
								onClick={onOpenTaskDrawer}
							/>
						))}
					</Box>
				))
			)}
		</QuadPanel>
	);
};

WorkQueueWidget.propTypes = {
	onOpenTaskDrawer: PropTypes.func.isRequired,
};

export default WorkQueueWidget;
